// src/utils/translation.js
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * 언어 파라미터를 사용하여 특정 언어로 고정된 번역 함수를 생성하는 커스텀 훅
 * 하이픈이 포함된 언어 코드(zh-cn, zh-tw)를 올바르게 처리합니다.
 * 
 * @param {string} namespace - i18n 네임스페이스 (예: 'common', 'benefits', 'about')
 * @returns {Function} - 번역 함수 t(key, options?)
 */
export function useLangTranslation(namespace) {
  const { lang = 'en' } = useParams();
  const { i18n } = useTranslation(namespace);

  /**
   * 리소스 번들에서 직접 번역 값을 가져오는 헬퍼 함수
   * getFixedT가 zh-cn을 제대로 인식하지 못하는 경우를 대비하여 사용
   */
  const getTranslation = (key, ns = namespace) => {
    if (i18n.hasResourceBundle(lang, ns)) {
      const bundle = i18n.getResourceBundle(lang, ns);
      const keys = key.split('.');
      let value = bundle;
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // fallback to i18n default behavior
          return i18n.t(key, { lng: lang, ns });
        }
      }
      
      return value || i18n.t(key, { lng: lang, ns });
    }
    
    return i18n.t(key, { lng: lang, ns });
  };

  /**
   * 번역 함수
   * @param {string} key - 번역 키
   * @param {object} options - 번역 옵션 (lng이 있으면 해당 언어 사용)
   */
  const t = (key, options) => {
    if (options && options.lng) {
      // 옵션에 lng이 있으면 그대로 사용
      return i18n.t(key, { ...options, ns: namespace });
    }
    return getTranslation(key, namespace);
  };

  return t;
}

